import { InputState, isCoarsePointer, isCompactHud } from "./input";

export type HudHooks = {
  setPlan: (on: boolean) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  frame: () => void;
};

const DEAD = 0.14;
const SPRINT_AT = 0.78;

export class TouchHud {
  private input: InputState;
  private stick: HTMLElement;
  private knob: HTMLElement;
  private sheet: HTMLElement;
  private grip: HTMLButtonElement;
  private hint: HTMLElement | null;
  private planBtn: HTMLButtonElement;
  private flyBtn: HTMLButtonElement;
  private skipGripClick = false;

  constructor(input: InputState, hooks: HudHooks) {
    this.input = input;
    this.stick = mustEl("stick");
    this.knob = mustEl("stick-knob");
    this.sheet = mustEl("sheet");
    this.grip = mustEl("sheet-toggle") as HTMLButtonElement;
    this.hint = document.getElementById("look-hint");
    this.planBtn = mustEl("view-plan") as HTMLButtonElement;
    this.flyBtn = mustEl("view-fly") as HTMLButtonElement;

    this.bindStick();
    this.bindHold(mustEl("lift-up"), () => {
      this.input.analogY = 1;
    }, () => {
      if (this.input.analogY > 0) this.input.analogY = 0;
    });
    this.bindHold(mustEl("lift-down"), () => {
      this.input.analogY = -1;
    }, () => {
      if (this.input.analogY < 0) this.input.analogY = 0;
    });
    this.bindRepeat(mustEl("zoom-in"), hooks.zoomIn);
    this.bindRepeat(mustEl("zoom-out"), hooks.zoomOut);
    mustEl("frame-view").addEventListener("click", () => hooks.frame());

    this.planBtn.addEventListener("click", () => hooks.setPlan(true));
    this.flyBtn.addEventListener("click", () => hooks.setPlan(false));

    this.bindSheet();
    this.syncChrome();
    window.matchMedia("(pointer: coarse), (max-width: 720px)").addEventListener("change", () => {
      this.syncChrome();
    });
  }

  setMode(plan: boolean): void {
    this.input.reset();
    this.resetKnob();
    this.planBtn.setAttribute("aria-pressed", plan ? "true" : "false");
    this.flyBtn.setAttribute("aria-pressed", plan ? "false" : "true");
    this.hideHint();
  }

  dismissHint(): void {
    this.hideHint();
  }

  private syncChrome(): void {
    document.body.classList.toggle("is-compact", isCompactHud());
    document.body.classList.toggle("is-coarse", isCoarsePointer());
  }

  private bindStick(): void {
    const el = this.stick;
    el.addEventListener("pointerdown", (e) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      el.setPointerCapture(e.pointerId);
      el.classList.add("is-active");
      this.sampleStick(e);
    });
    el.addEventListener("pointermove", (e) => {
      if (!el.hasPointerCapture(e.pointerId)) return;
      this.sampleStick(e);
    });
    const end = (e: PointerEvent) => {
      if (!el.hasPointerCapture(e.pointerId) && e.type !== "pointercancel") return;
      if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
      this.input.analogX = 0;
      this.input.analogZ = 0;
      this.input.sprint = false;
      el.classList.remove("is-active");
      this.resetKnob();
    };
    el.addEventListener("pointerup", end);
    el.addEventListener("pointercancel", end);
  }

  private sampleStick(e: PointerEvent): void {
    const r = this.stick.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const max = r.width * 0.36;
    let dx = e.clientX - cx;
    let dy = e.clientY - cy;
    const len = Math.hypot(dx, dy);
    if (len > max && len > 0) {
      dx = (dx / len) * max;
      dy = (dy / len) * max;
    }
    this.knob.style.transform = `translate(${dx}px, ${dy}px)`;

    const nx = dx / max;
    const ny = dy / max;
    const mag = Math.hypot(nx, ny);
    if (mag < DEAD) {
      this.input.analogX = 0;
      this.input.analogZ = 0;
      this.input.sprint = false;
      return;
    }
    const gain = (mag - DEAD) / (1 - DEAD);
    const scale = gain / mag;
    this.input.analogX = clamp(nx * scale, -1, 1);
    this.input.analogZ = clamp(ny * scale, -1, 1);
    this.input.sprint = mag >= SPRINT_AT;
  }

  private resetKnob(): void {
    this.knob.style.transform = "translate(0, 0)";
  }

  private bindHold(el: HTMLElement, on: () => void, off: () => void): void {
    const start = (e: PointerEvent) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      e.preventDefault();
      el.setPointerCapture(e.pointerId);
      el.classList.add("is-held");
      on();
    };
    const stop = () => {
      el.classList.remove("is-held");
      off();
    };
    el.addEventListener("pointerdown", start);
    el.addEventListener("pointerup", stop);
    el.addEventListener("pointercancel", stop);
  }

  private bindRepeat(el: HTMLElement, fn: () => void): void {
    let timer = 0;
    const start = (e: PointerEvent) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      e.preventDefault();
      el.setPointerCapture(e.pointerId);
      el.classList.add("is-held");
      fn();
      timer = window.setInterval(fn, 160);
    };
    const stop = () => {
      el.classList.remove("is-held");
      if (timer) {
        clearInterval(timer);
        timer = 0;
      }
    };
    el.addEventListener("pointerdown", start);
    el.addEventListener("pointerup", stop);
    el.addEventListener("pointercancel", stop);
  }

  private bindSheet(): void {
    let y0 = 0;
    let dragged = false;

    this.grip.addEventListener("pointerdown", (e) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      y0 = e.clientY;
      dragged = false;
      this.grip.setPointerCapture(e.pointerId);
    });
    this.grip.addEventListener("pointermove", (e) => {
      if (!this.grip.hasPointerCapture(e.pointerId)) return;
      if (Math.abs(e.clientY - y0) > 12) dragged = true;
    });
    this.grip.addEventListener("pointerup", (e) => {
      if (!dragged) return;
      this.skipGripClick = true;
      const dy = e.clientY - y0;
      if (dy < -20) this.setSheet(true);
      else if (dy > 20) this.setSheet(false);
    });
    this.grip.addEventListener("click", (e) => {
      if (this.skipGripClick) {
        this.skipGripClick = false;
        e.preventDefault();
        return;
      }
      this.setSheet(!this.sheet.classList.contains("is-open"));
    });
  }

  private setSheet(open: boolean): void {
    this.sheet.classList.toggle("is-open", open);
    document.body.classList.toggle("sheet-open", open);
    this.grip.setAttribute("aria-expanded", open ? "true" : "false");
  }

  private hideHint(): void {
    if (!this.hint) return;
    this.hint.classList.remove("is-on");
    this.hint.hidden = true;
  }
}

function clamp(n: number, a: number, b: number): number {
  return Math.min(b, Math.max(a, n));
}

function mustEl(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Missing #${id}`);
  return el;
}
